import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { timelineActionTypes } from '@store/Timeline/Timeline.actionTypes';
import { useMemberCompanyTimezones } from '@/hooks/useMemberCompanyTimezones';
import { useMembersOptionsForSelect } from '@/hooks/useMembersOptionsForSelect';
import memberSessionsService from '@/services/memberSessions';
import { filterScreenshotsByTimeRange } from './Screenshots.helper';
import {
  useUrlDateRangeSync,
  useUrlMemberHydrationSync,
  useUrlMemberNavSync,
  useUrlTimezoneIgnoreFlagSync,
  useUrlFilterWrite,
  getDefaultMemberFromOptions,
} from '@/hooks/useUrlFilterSync';
import {
  getInitialReportDateRangeFromSearch,
  getReportFiltersFromSearch,
} from '@/utils/reportFiltersUrl';
import { collectScreenshotsFromActivities } from '@/utils/screenshotPrivacy';
import { ScreenshotsView } from './Screenshots.view';

export const ScreenshotsComponent = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const { sessionsByDay, loading, filters } = useSelector(
    (state) => state.timeline
  );
  const team = useSelector((state) => state.team);
  const auth = useSelector((state) => state.auth);

  const teamId = team?.active?.activeTeamid;
  const teamName = team?.active?.Team;
  const companyId = auth?.profile?.activeCompany?.companyId;
  const [selectedMember, setSelectedMember] = useState(null);
  const memberId = selectedMember?.value || null;
  const employeeName = selectedMember?.label?.replace(/\s*\([^)]*\)\s*$/, '');

  const {
    membersOptions,
    isLoading: membersLoading,
    isError: membersError,
    error: membersErrorObj,
    refetch: refetchMembers,
  } = useMembersOptionsForSelect(teamId);

  const membersOptionsRef = useRef(membersOptions);
  membersOptionsRef.current = membersOptions;

  const [range, setRange] = useState(() =>
    getInitialReportDateRangeFromSearch(location.search)
  );

  const [ignoreUrlTimezone, setIgnoreUrlTimezone] = useState(false);
  const userOverrideTimezoneRef = useRef(false);

  useUrlDateRangeSync({ location, setDateRange: setRange });
  useUrlMemberHydrationSync({
    location,
    enabled: Boolean(teamId),
    membersOptions,
    selectedMember,
    setSelectedMember,
    currentUserId: auth?.profile?.id,
    getDefaultMember: getDefaultMemberFromOptions,
  });
  useUrlMemberNavSync({
    location,
    enabled: Boolean(teamId),
    membersOptionsRef,
    setSelectedMember,
  });
  useUrlTimezoneIgnoreFlagSync({ history, setIgnoreUrlTimezone });

  const filtersFromUrl = useMemo(
    () => getReportFiltersFromSearch(location.search),
    [location.search]
  );
  const urlTimezoneForHook = useMemo(
    () => (!ignoreUrlTimezone && filtersFromUrl.tz ? filtersFromUrl.tz : null),
    [ignoreUrlTimezone, filtersFromUrl.tz]
  );

  const {
    timezoneOptions,
    selectedTimezone,
    setSelectedTimezone,
    timezonesLoading,
  } = useMemberCompanyTimezones({
    teamId,
    memberId,
    companyId,
    initialTimezone: urlTimezoneForHook || filters?.timeZone || null,
    autoSelectMemberTimezone: true,
    userOverrideTimezoneRef,
  });

  const writeParams = useMemo(() => {
    const params = { start: range.start, end: range.end };
    if (selectedMember?.value) params.member = selectedMember.value;
    if (selectedTimezone) params.tz = selectedTimezone;
    return params;
  }, [range.start, range.end, selectedMember?.value, selectedTimezone]);

  useUrlFilterWrite({
    history,
    location,
    params: writeParams,
    enabled: Boolean(teamId && range?.start && range?.end),
  });

  const onMemberFilterChange = useCallback(
    (opt) => {
      userOverrideTimezoneRef.current = false;
      setIgnoreUrlTimezone(true);
      setSelectedMember(opt);
    },
    [setSelectedMember]
  );

  const handleTimezoneFilterChange = useCallback(
    (value) => {
      userOverrideTimezoneRef.current = true;
      setIgnoreUrlTimezone(true);
      setSelectedTimezone(value);
    },
    [setSelectedTimezone, setIgnoreUrlTimezone, userOverrideTimezoneRef]
  );

  const [sessionScreenshots, setSessionScreenshots] = useState({});
  const [loadingScreenshots, setLoadingScreenshots] = useState({});
  const [expandedSessions, setExpandedSessions] = useState(new Set());
  const [screenshotModalOpen, setScreenshotModalOpen] = useState(false);
  const [selectedScreenshotIndex, setSelectedScreenshotIndex] = useState(0);
  const [allScreenshotsList, setAllScreenshotsList] = useState([]);
  const [modalOpenToken, setModalOpenToken] = useState(0);

  const sessionScreenshotsRef = useRef(sessionScreenshots);
  const loadingScreenshotsRef = useRef(loadingScreenshots);
  const sessionActivitiesCacheRef = useRef({});
  const hasSeededExpansionRef = useRef(false);
  sessionScreenshotsRef.current = sessionScreenshots;
  loadingScreenshotsRef.current = loadingScreenshots;

  const getSessionActivitiesCacheKey = (sessionId, timezone) =>
    `${sessionId}::${timezone || 'UTC'}`;

  const getEmployeeIdForUser = (userId) => {
    if (!userId) return '';
    const match = membersOptions.find((m) => m.value === userId);
    return match?.employeeId ?? userId;
  };

  const getRateForUser = (userId) => {
    const match = membersOptions.find((m) => m.value === userId);
    return match?.rate || 0;
  };

  const fetchTimeline = () => {
    if (!selectedMember) return;
    const employeeId = getEmployeeIdForUser(selectedMember.value);
    dispatch({
      type: timelineActionTypes.GET_SESSIONS,
      payload: {
        teamId,
        userUID: selectedMember.value,
        companyId,
        employeeId,
        memberId: selectedMember.value,
        startDate: range.start,
        endDate: range.end,
        timeZone: selectedTimezone,
        page: 1,
        pageSize: 1000, // Large page size to get all sessions
        rate: getRateForUser(selectedMember.value),
      },
    });
  };

  useEffect(() => {
    if (selectedMember) {
      // Clear screenshots and expanded sessions when filters change
      setSessionScreenshots({});
      setLoadingScreenshots({});
      setExpandedSessions(new Set());
      sessionActivitiesCacheRef.current = {};
      hasSeededExpansionRef.current = false;
      fetchTimeline();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMember, selectedTimezone, range.start, range.end]);

  // Fetch screenshots for a session
  const fetchSessionScreenshots = async (
    sessionId,
    parentSessionId = null,
    segmentStart = null,
    segmentEnd = null
  ) => {
    const actualSessionId = parentSessionId || sessionId;
    const isSplitSegment = parentSessionId && sessionId !== actualSessionId;
    const shotsMap = sessionScreenshotsRef.current;
    const loadingMap = loadingScreenshotsRef.current;
    const cacheKey = getSessionActivitiesCacheKey(
      actualSessionId,
      selectedTimezone
    );

    if (isSplitSegment && shotsMap[actualSessionId]) {
      const allScreenshots = shotsMap[actualSessionId];
      const filteredScreenshots = filterScreenshotsByTimeRange(
        allScreenshots,
        segmentStart,
        segmentEnd,
        selectedTimezone
      );
      setSessionScreenshots((prev) => ({
        ...prev,
        [sessionId]: filteredScreenshots,
      }));
      return;
    }

    if (shotsMap[actualSessionId] || loadingMap[actualSessionId]) {
      return;
    }

    if (!teamId || !selectedMember?.value || !actualSessionId) {
      return;
    }

    if (sessionActivitiesCacheRef.current[cacheKey]) {
      const cachedAll = sessionActivitiesCacheRef.current[cacheKey];
      setSessionScreenshots((prev) => {
        const updated = { ...prev, [actualSessionId]: cachedAll };
        if (isSplitSegment && segmentStart && segmentEnd) {
          updated[sessionId] = filterScreenshotsByTimeRange(
            cachedAll,
            segmentStart,
            segmentEnd,
            selectedTimezone
          );
        }
        return updated;
      });
      return;
    }

    setLoadingScreenshots((prev) => ({ ...prev, [actualSessionId]: true }));

    let sessionStartTime = null;
    if (sessionsByDay) {
      for (const day of sessionsByDay) {
        const foundSession = day.sessions?.find(
          (s) => s.id === actualSessionId || s.id === sessionId
        );
        if (foundSession?.start) {
          sessionStartTime = foundSession.start;
          break;
        }
      }
    }

    try {
      const employeeId = getEmployeeIdForUser(selectedMember.value);
      const result = await memberSessionsService.getSessionActivities({
        teamId,
        userUID: selectedMember.value,
        companyId,
        employeeId,
        sessionId: actualSessionId,
        sessionStartTime,
      });

      if (result.success) {
        const activities = result.data.activities || [];
        const allScreenshots = collectScreenshotsFromActivities(activities);

        sessionActivitiesCacheRef.current[cacheKey] = allScreenshots;

        setSessionScreenshots((prev) => {
          const updated = { ...prev, [actualSessionId]: allScreenshots };

          if (isSplitSegment && segmentStart && segmentEnd) {
            const filteredScreenshots = filterScreenshotsByTimeRange(
              allScreenshots,
              segmentStart,
              segmentEnd,
              selectedTimezone
            );
            updated[sessionId] = filteredScreenshots;
          }

          return updated;
        });
      }
    } catch (error) {
      console.error('Error loading session screenshots', error);
    } finally {
      setLoadingScreenshots((prev) => {
        const updated = { ...prev };
        delete updated[actualSessionId];
        if (isSplitSegment) {
          delete updated[sessionId];
        }
        return updated;
      });
    }
  };

  // Fetch screenshots only for sessions whose accordion is open
  useEffect(() => {
    if (loading || !sessionsByDay?.length) return;

    sessionsByDay.forEach((day) => {
      day.sessions.forEach((session) => {
        if (!session.id || !expandedSessions.has(session.id)) return;

        const actualSessionId = session.parentSessionId || session.id;
        const isSplitSegment = session.isSplit && session.parentSessionId;
        const shotsMap = sessionScreenshotsRef.current;
        const loadingMap = loadingScreenshotsRef.current;

        if (!shotsMap[actualSessionId] && !loadingMap[actualSessionId]) {
          fetchSessionScreenshots(
            session.id,
            session.parentSessionId,
            isSplitSegment ? session.start : null,
            isSplitSegment ? session.end : null
          );
        } else if (
          isSplitSegment &&
          !shotsMap[session.id] &&
          shotsMap[actualSessionId]
        ) {
          fetchSessionScreenshots(
            session.id,
            session.parentSessionId,
            session.start,
            session.end
          );
        }
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, sessionsByDay, selectedTimezone, expandedSessions]);

  // Open screenshot modal
  const openScreenshotModal = (clickedIndex, screenshots) => {
    const allScreenshots = [];
    sessionsByDay.forEach((day) => {
      day.sessions.forEach((session) => {
        const sessionScreens = sessionScreenshots[session.id] || [];
        sessionScreens.forEach((screenshot) => {
          allScreenshots.push({
            ...screenshot,
            sessionId: session.id,
            dayLabel: day.dayLabel,
          });
        });
      });
    });

    setAllScreenshotsList(allScreenshots);

    const clickedScreenshot = screenshots[clickedIndex];
    const foundIndex = allScreenshots.findIndex(
      (s) =>
        s.url === clickedScreenshot.url &&
        s.timestamp === clickedScreenshot.timestamp &&
        s.activityId === clickedScreenshot.activityId &&
        s.screenIndex === clickedScreenshot.screenIndex
    );

    const idx = foundIndex >= 0 ? foundIndex : 0;

    setSelectedScreenshotIndex(idx);
    setScreenshotModalOpen(true);
    setModalOpenToken((token) => token + 1);
  };

  // Close screenshot modal
  const closeScreenshotModal = () => {
    setScreenshotModalOpen(false);
    setSelectedScreenshotIndex(0);
    setAllScreenshotsList([]);
  };

  const flattenedSessions = useMemo(() => {
    const rows = [];
    (sessionsByDay ?? []).forEach((day) => {
      day.sessions.forEach((session) => {
        rows.push({ dayLabel: day.dayLabel, dayKey: day.dayKey, session });
      });
    });
    return rows;
  }, [sessionsByDay]);

  // The first session is auto-opened by the Accordion's isOpenOnStart, but
  // that doesn't call onOpenChange — seed expandedSessions to match so its
  // screenshots actually get fetched and its heading renders as "expanded".
  // Guarded by a ref (reset only on filter change) so a Refresh-triggered
  // sessionsByDay reference change doesn't stomp on a manual collapse.
  useEffect(() => {
    if (!flattenedSessions.length || hasSeededExpansionRef.current) return;
    hasSeededExpansionRef.current = true;
    setExpandedSessions((prev) =>
      prev.size > 0 ? prev : new Set([flattenedSessions[0].session.id])
    );
  }, [flattenedSessions]);

  const onSessionOpenChange = (sessionId, open) => {
    setExpandedSessions((prev) => {
      const next = new Set(prev);
      if (open) {
        next.add(sessionId);
      } else {
        next.delete(sessionId);
      }
      return next;
    });
  };

  return (
    <ScreenshotsView
      membersOptions={membersOptions}
      selectedMember={selectedMember}
      onMemberFilterChange={onMemberFilterChange}
      membersLoading={membersLoading}
      membersError={membersError}
      membersErrorObj={membersErrorObj}
      refetchMembers={refetchMembers}
      timezoneOptions={timezoneOptions}
      selectedTimezone={selectedTimezone}
      onTimezoneChange={handleTimezoneFilterChange}
      timezonesLoading={timezonesLoading}
      date={range.start}
      onDateChange={(dateStr) => setRange({ start: dateStr, end: dateStr })}
      onRefresh={fetchTimeline}
      loading={loading}
      flattenedSessions={flattenedSessions}
      sessionScreenshots={sessionScreenshots}
      loadingScreenshots={loadingScreenshots}
      expandedSessions={expandedSessions}
      onSessionOpenChange={onSessionOpenChange}
      onOpenScreenshotModal={openScreenshotModal}
      screenshotModalOpen={screenshotModalOpen}
      closeScreenshotModal={closeScreenshotModal}
      allScreenshotsList={allScreenshotsList}
      selectedScreenshotIndex={selectedScreenshotIndex}
      setSelectedScreenshotIndex={setSelectedScreenshotIndex}
      employeeName={employeeName}
      teamName={teamName}
      modalOpenToken={modalOpenToken}
    />
  );
};
